import type { ReactNode } from 'react';

import type { LeadFormState } from '@core/Atoms/Pdp/FormState.atom';
import type { VehicleDealerOpeningTime } from '@core/Entities/Vehicle/VehicleDealerOpeningTime.entity';
import { isBusinessHours } from '@core/Utils/isBusinessHours/isBusinessHours';
import { CallBack } from '@gds/Icons';
import { Call } from '@gds/Icons/Paths/Call';
import type { AllFlags } from '@growthBookExperimentation/Entities/FlagKeys.entities';
import type { StringsType } from '@lead-forms/Entities/LeadForm';

export type LeadCtaRequiredFields = (keyof LeadFormState)[];

export type callDealerCtasConfigValue = {
  labelId: (isInstaMotion: boolean) => string;
  shouldShowCta: (props: { openingTimes: VehicleDealerOpeningTime[]; flags?: AllFlags }) => boolean;
  variant: (props: { isEcom: boolean; isUk: boolean }) => 'outlined' | 'contained' | 'link';
  requiredFields?: () => LeadCtaRequiredFields;
  buttonIcon?: ReactNode;
  onErrorModalAction?: (strings: StringsType) => ReactNode;
  requestCallback: (
    dealerNumberError: boolean,
    openingTimes?: VehicleDealerOpeningTime[],
  ) => boolean;
  requestCallbackButtonLabel: (
    strings: StringsType,
    isSticky: boolean,
    isEcom: boolean,
    hasReserveNow?: boolean,
  ) => string;
  requestCallbackLeftIcon: (
    isSticky: boolean,
    isEcom: boolean,
    hasReserveNow?: boolean,
    isVsp?: boolean,
  ) => ReactNode | null;
  shouldShowRequestCallbackModal: (windowInnerWidth: number, isSticky: boolean) => boolean;
  shouldShowPhoneNumberError: (dealerNumberError: boolean, isSticky: boolean) => boolean;
  showButtonText?: (props: {
    isVsp?: boolean;
    isEcom?: boolean;
    isSticky?: boolean;
    isUk?: boolean;
  }) => boolean | undefined;
};

export type CtaPlacement = 'pdp_call-dealer' | 'hov_call-dealer';
export type CallDealerCtasConfig = Record<CtaPlacement, callDealerCtasConfigValue>;

export type callDealerCtasConfigArray = Array<[CtaPlacement, callDealerCtasConfigValue]>;

export const callDealerCtasConfig = {
  'pdp_call-dealer': {
    labelId: isInstaMotion => (isInstaMotion ? 'callDealerButtonInstamotion' : 'callDealerButton'),
    shouldShowCta: ({ openingTimes }) => isBusinessHours(openingTimes),
    variant: ({ isEcom, isUk }) => (!isUk && !isEcom ? 'contained' : 'outlined'),
    showButtonText: ({ isSticky, isVsp, isEcom }) =>
      !isSticky || (isVsp && isSticky) || (isSticky && !isEcom),
    buttonIcon: <Call />,
    requestCallback: dealerNumberError => dealerNumberError,
    requestCallbackLeftIcon: (isSticky, isEcom, isVsp, hasReserveNow) => {
      return (isSticky && isEcom && !isVsp) || hasReserveNow ? <CallBack /> : null;
    },
    shouldShowRequestCallbackModal: (windowInnerWidth, isSticky) =>
      !!(windowInnerWidth && windowInnerWidth < 768 && isSticky),
    shouldShowPhoneNumberError: (dealerNumberError, isSticky) => dealerNumberError && !isSticky,
    requestCallbackButtonLabel: (strings, isSticky, isEcom, hasReserveNow) =>
      (isSticky && isEcom) || hasReserveNow ? '' : strings.requestCallbackButton,
  },
  'hov_call-dealer': {
    labelId: () => 'historyCheckModalCallDealerButton',
    shouldShowCta: () => true,
    variant: () => 'contained',
    showButtonText: ({ isSticky, isVsp, isEcom }) =>
      !isSticky || (isVsp && isSticky) || (isSticky && !isEcom),
    buttonIcon: <Call />,
    requestCallback: (dealerNumberError, openingTimes) =>
      dealerNumberError || !isBusinessHours(openingTimes),
    requestCallbackLeftIcon: () => <CallBack />,
    shouldShowPhoneNumberError: () => false,
    requestCallbackButtonLabel: strings => strings.requestCallbackButton,
    shouldShowRequestCallbackModal: () => true,
  },
} as const satisfies CallDealerCtasConfig;
